'use client'

import { useId, useState, type FormEvent } from 'react'
import PhoneInput from '@sifly/shared-ui/content/LazyPhoneInput'
import type { FulfillmentMethod, PostalAddress } from '@sifly/shared-types'
import { validateCustomerDetails, PHONE_REQUIRED, CHOOSE_STATE, CHOOSE_PROVINCE, type ManualCustomerDetails } from '@/lib/manualCheckout'
import { normalizeSubdivision } from '@sifly/shared-utils'
import { SubdivisionOptions } from './AddressFields'

const INPUT_CLASS =
  'w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated,#fff)] px-3 py-2.5 text-sm text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-cta-edge)] focus:border-[var(--color-cta-edge)]'
/** INPUT_CLASS's box on PhoneInput's wrapper (it draws the one border and ring). */
const PHONE_CLASS = 'rounded-md! border-[var(--color-border)]! focus-within:border-[var(--color-cta-edge)]! bg-[var(--color-surface-elevated,#fff)]!'
const LABEL_CLASS = 'block text-sm font-medium text-[var(--color-text-secondary)] mb-1'
const ERROR_CLASS = 'mt-1 text-xs text-[var(--color-error-strong)]'

/**
 * Which field group a `validateCustomerDetails` message belongs next to.
 * This is a PRESENTATION lookup only — it never decides pass/fail, that stays
 * entirely with the imported function, so client and server can't disagree
 * about what's acceptable. An unrecognized message (the function's wording
 * changed and this table didn't) falls back to 'general': still shown, just
 * not perfectly anchored — fails loud, never silently swallowed.
 */
type FieldAnchor = 'email' | 'name' | 'phone' | 'address' | 'general'

const FIELD_ANCHORS: Record<string, FieldAnchor> = {
  'A valid email is required so we can confirm your order.': 'email',
  'That email address is too long.': 'email',
  'A name is required.': 'name',
  'That name is too long.': 'name',
  'That phone number is too long.': 'phone',
  [PHONE_REQUIRED]: 'phone',
  'A shipping address is required.': 'address',
  'A complete shipping address is required.': 'address',
  'We can only ship within the US and Canada.': 'address',
  'That address is too long.': 'address',
  'That city is too long.': 'address',
  'That state is too long.': 'address',
  [CHOOSE_STATE]: 'address',
  [CHOOSE_PROVINCE]: 'address',
  'That postal code is too long.': 'address',
}

function anchorForError(message: string): FieldAnchor {
  return FIELD_ANCHORS[message] ?? 'general'
}

/**
 * Email / name / phone / shipping-address collection for a manual (non-Stripe)
 * checkout. Pulled out of `CartDrawer` rather than grown into it.
 *
 * `onSubmit` receives the fully validated, normalized `ManualCustomerDetails`
 * — the parent should build its request body from THAT argument, not from
 * `value`. Calling `onChange` and `onSubmit` back to back in the same tick
 * would otherwise race React's state batching: the parent's `value` state
 * wouldn't reflect the just-validated (trimmed, defaulted-country) object
 * until the next render, by which time the fetch has already fired.
 */
export default function CustomerDetailsForm({
  value,
  onChange,
  onSubmit,
  submitting,
  error,
  fulfillmentMethod = 'ship',
  submitLabel = 'Place order',
  beforeSubmit,
}: {
  value: Partial<ManualCustomerDetails>
  onChange: (value: Partial<ManualCustomerDetails>) => void
  onSubmit: (details: ManualCustomerDetails) => void
  submitting: boolean
  error?: string | null
  /** 'pickup' hides the address. */
  fulfillmentMethod?: FulfillmentMethod
  /** /checkout names the method on the button ("Place order · Pay by Zelle"). */
  submitLabel?: string
  /** Rendered between the fields and the button (the totals block on /checkout). */
  beforeSubmit?: React.ReactNode
}) {
  const idPrefix = useId()
  const [attempted, setAttempted] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const address: Partial<PostalAddress> = value.shipping_address ?? {}
  const email = value.email ?? ''
  const name = value.name ?? ''
  const phone = value.phone ?? ''
  const line1 = address.line1 ?? ''
  const line2 = address.line2 ?? ''
  const city = address.city ?? ''
  const state = address.state ?? ''
  const postalCode = address.postal_code ?? ''
  // Restricted to US/CA below (the only options the <select> offers), so a
  // client-side "invalid country" state is unreachable — the shipping-country
  // gate in validateCustomerDetails only ever fires server-side against a
  // forged request.
  const country = address.country ?? 'US'

  function update(patch: Partial<ManualCustomerDetails>) {
    setFormError(null)
    onChange({ ...value, ...patch })
  }
  function updateAddress(patch: Partial<PostalAddress>) {
    setFormError(null)
    onChange({ ...value, shipping_address: { ...address, ...patch } })
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setAttempted(true)

    const candidate: ManualCustomerDetails = {
      email: email.trim(),
      name: name.trim(),
      phone: phone.trim() || null,
      shipping_address: fulfillmentMethod === 'pickup' ? null : {
        line1: line1.trim(),
        line2: line2.trim() || undefined,
        city: city.trim(),
        state: state.trim(),
        postal_code: postalCode.trim(),
        country,
      },
    }

    const result = validateCustomerDetails(candidate, fulfillmentMethod)
    if (!result.ok) {
      setFormError(result.error)
      return
    }
    setFormError(null)
    onSubmit(result.value)
  }

  const anchor = attempted && formError ? anchorForError(formError) : null

  const emailId = `${idPrefix}-email`
  const nameId = `${idPrefix}-name`
  const phoneId = `${idPrefix}-phone`
  const line1Id = `${idPrefix}-line1`
  const line2Id = `${idPrefix}-line2`
  const cityId = `${idPrefix}-city`
  const stateId = `${idPrefix}-state`
  const postalId = `${idPrefix}-postal`
  const countryId = `${idPrefix}-country`
  const emailErrorId = `${emailId}-error`
  const nameErrorId = `${nameId}-error`
  const phoneErrorId = `${phoneId}-error`
  const addressErrorId = `${idPrefix}-address-error`
  const generalErrorId = `${idPrefix}-form-error`

  return (
    <form onSubmit={handleSubmit} className="space-y-3" noValidate>
      <div>
        <label htmlFor={emailId} className={LABEL_CLASS}>Email</label>
        <input
          id={emailId}
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => update({ email: e.target.value })}
          aria-invalid={anchor === 'email' || undefined}
          aria-describedby={anchor === 'email' ? emailErrorId : undefined}
          className={INPUT_CLASS}
        />
        {anchor === 'email' && <p id={emailErrorId} className={ERROR_CLASS}>{formError}</p>}
      </div>

      <div>
        <label htmlFor={nameId} className={LABEL_CLASS}>Full name</label>
        <input
          id={nameId}
          type="text"
          required
          autoComplete="name"
          value={name}
          onChange={(e) => update({ name: e.target.value })}
          aria-invalid={anchor === 'name' || undefined}
          aria-describedby={anchor === 'name' ? nameErrorId : undefined}
          className={INPUT_CLASS}
        />
        {anchor === 'name' && <p id={nameErrorId} className={ERROR_CLASS}>{formError}</p>}
      </div>

      <div>
        <label htmlFor={phoneId} className={LABEL_CLASS}>Phone</label>
        <PhoneInput
          id={phoneId}
          value={phone}
          onChange={(e164) => update({ phone: e164 || null })}
          defaultCountry="US"
          favoriteCountries={['US', 'CA']}
          invalid={anchor === 'phone'}
          className={PHONE_CLASS}
          required
          describedById={anchor === 'phone' ? phoneErrorId : undefined}
        />
        {anchor === 'phone' && <p id={phoneErrorId} className={ERROR_CLASS}>{formError}</p>}
      </div>

      {fulfillmentMethod !== 'pickup' && (
        <fieldset aria-describedby={anchor === 'address' ? addressErrorId : undefined}>
          <legend className={LABEL_CLASS}>Shipping address</legend>
          {anchor === 'address' && (
            <p id={addressErrorId} className="mb-1.5 text-xs text-[var(--color-error-strong)]">{formError}</p>
          )}
          <div className="space-y-2">
            <div>
              <label htmlFor={line1Id} className="sr-only">Address line 1</label>
              <input
                id={line1Id}
                type="text"
                required
                autoComplete="address-line1"
                placeholder="Address line 1"
                value={line1}
                onChange={(e) => updateAddress({ line1: e.target.value })}
                aria-invalid={anchor === 'address' || undefined}
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label htmlFor={line2Id} className="sr-only">Address line 2 (optional)</label>
              <input
                id={line2Id}
                type="text"
                autoComplete="address-line2"
                placeholder="Address line 2 (optional)"
                value={line2}
                onChange={(e) => updateAddress({ line2: e.target.value })}
                aria-invalid={anchor === 'address' || undefined}
                className={INPUT_CLASS}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor={cityId} className="sr-only">City</label>
                <input
                  id={cityId}
                  type="text"
                  required
                  autoComplete="address-level2"
                  placeholder="City"
                  value={city}
                  onChange={(e) => updateAddress({ city: e.target.value })}
                  aria-invalid={anchor === 'address' || undefined}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label htmlFor={stateId} className="sr-only">State / Province</label>
                <select
                  id={stateId}
                  required
                  autoComplete="address-level1"
                  value={normalizeSubdivision(country, state) ?? ''}
                  onChange={(e) => updateAddress({ state: e.target.value })}
                  aria-invalid={anchor === 'address' || undefined}
                  className={INPUT_CLASS}
                >
                  <SubdivisionOptions country={country} />
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor={postalId} className="sr-only">Postal code</label>
                <input
                  id={postalId}
                  type="text"
                  required
                  autoComplete="postal-code"
                  placeholder="Postal code"
                  value={postalCode}
                  onChange={(e) => updateAddress({ postal_code: e.target.value })}
                  aria-invalid={anchor === 'address' || undefined}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label htmlFor={countryId} className="sr-only">Country</label>
                <select
                  id={countryId}
                  autoComplete="country"
                  value={country}
                  onChange={(e) => updateAddress({ country: e.target.value, state: normalizeSubdivision(e.target.value, state) ?? '' })}
                  className={INPUT_CLASS}
                >
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                </select>
              </div>
            </div>
          </div>
        </fieldset>
      )}

      {error && (
        <p className="text-xs text-[var(--color-error-strong)] bg-[var(--color-error-surface)] border border-[var(--color-error-border)] rounded-md px-3 py-2">
          {error}
        </p>
      )}
      {anchor === 'general' && (
        <p id={generalErrorId} className="text-xs text-[var(--color-error-strong)]">{formError}</p>
      )}

      {beforeSubmit}
      <button
        type="submit"
        disabled={submitting}
        className="cta-primary w-full py-3 rounded-xl font-medium disabled:opacity-50 transition-opacity"
      >
        {submitting ? 'Placing order...' : submitLabel}
      </button>
    </form>
  )
}
